import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { showConfirmAlert, showErrorAlert, showSuccessAlert, showMaintenanceSelectAlert } from '../../utils/alerts.js';
import { getBuildingsList } from '../../utils/api.js';
import { enableMaintenanceMode, disableMaintenanceMode } from '../../utils/services/buildingService.js';
import { EVENT_TYPE } from '../../utils/enums.js';
import socket from '../../utils/socket.js';

const Buildings = (props) => {
  const { history } = props;
  const [buildings, setBuildings] = useState([]);
  const [searchBuilding, setSearchBuilding] = useState('');

  const getAllBuildings = async () => {
    try {
      const data = await getBuildingsList();

      setBuildings(data);
    } catch (error) {
      console.error('Error', error);
      showErrorAlert('Error', error.message);
    }
  };

  const emitMuteEventForBuilding = async (event, building) => {
    try {
      if (!event || !building?.id) {
        showErrorAlert('¡Ups!', 'Tuvimos un problema con la información del edificio. Intenta de nuevo más tarde.');
        return;
      }

      let errorReceived = false;

      socket.once('user_event:error', () => {
        errorReceived = true;
        showErrorAlert(
          'Lo sentimos',
          'No pudimos silenciar la alarma en este momento. Por favor, vuelve a intentarlo pronto.',
        );
      });

      const data = {
        event,
        buildingId: building.id,
      };

      socket.emit('user_event', data);

      setTimeout(() => {
        if (!errorReceived) {
          showSuccessAlert(
            'Alarma silenciada!',
            `La alarma del edificio <b>${building.name}</b> fue silenciada correctamente.`,
          );
        }
      }, 1000);
    } catch (error) {
      showErrorAlert(
        '¡Algo salió mal!',
        'Ocurrió un error al intentar silenciar la alarma del edificio. Por favor, vuelve a intentarlo pronto.',
      );
    }
  };

  const emitRestarRpiEventForBuilding = async (event, building) => {
    try {
      if (!event || !building?.id) {
        showErrorAlert('¡Ups!', 'Tuvimos un problema con la información del edificio. Intenta de nuevo más tarde.');
        return;
      }

      let errorReceived = false;

      socket.once('user_event:error', () => {
        errorReceived = true;
        showErrorAlert(
          'Lo sentimos',
          'No pudimos reiniciar el edificio en este momento. Por favor, vuelve a intentarlo pronto.',
        );
      });

      const data = {
        event,
        buildingId: building.id,
      };

      socket.emit('user_event', data);

      setTimeout(() => {
        if (!errorReceived) {
          showSuccessAlert(
            'Edificio reiniciado!',
            `El edificio <b>${building.name}</b> ha sido reiniciado correctamente.`,
          );
        }
      }, 1000);
    } catch (error) {
      showErrorAlert(
        '¡Algo salió mal!',
        'Ocurrió un error al intentar reiniciar el edificio. Por favor, vuelve a intentarlo pronto.',
      );
    }
  };

  const confirmMuteBuilding = (building) => {
    showConfirmAlert({
      message: `Esta acción silenciará y desactivará la alarma del edificio ${building.name}.`,
      title: 'Advertencia',
      cancelButton: 'Cancelar',
      continueButton: 'Silenciar edificio',
      onConfirm: () => {
        emitMuteEventForBuilding(EVENT_TYPE.MUTE, building);
      },
    });
  };

  const confirmRestartRpiBuilding = (building) => {
    showConfirmAlert({
      message: `Esta acción reiniciara el edificio ${building.name}.`,
      title: 'Advertencia',
      cancelButton: 'Cancelar',
      continueButton: 'Reiniciar edificio',
      onConfirm: () => {
        emitRestarRpiEventForBuilding(EVENT_TYPE.RESTART_RPI, building);
      },
    });
  };

  const toggleMaintenanceMode = async (building) => {
    const isActive = building.maintenanceModeEndsAt && new Date(building.maintenanceModeEndsAt) > new Date();
    if (isActive) {
      showConfirmAlert({
        title: 'Cancelar Mantenimiento',
        message: '¿Estás seguro de que quieres reanudar las notificaciones para este edificio?',
        confirmText: 'Sí, reanudar',
        onConfirm: async () => {
          try {
            await disableMaintenanceMode(building.id);
            showSuccessAlert('Mantenimiento cancelado', 'Las notificaciones han sido restauradas.');
            getAllBuildings();
          } catch(err) {
            showErrorAlert('Error', 'No se pudo cancelar el mantenimiento.');
          }
        }
      });
    } else {
      showMaintenanceSelectAlert({
        onConfirm: async (duration) => {
          try {
            await enableMaintenanceMode(building.id, duration);
            showSuccessAlert('Mantenimiento activado', `Las notificaciones se han silenciado por ${duration} minutos.`);
            getAllBuildings();
          } catch(err) {
            showErrorAlert('Error', 'No se pudo activar el mantenimiento.');
          }
        }
      });
    }
  };

  useEffect(() => {
    getAllBuildings();
  }, []);

  const filteredBuildings = useMemo(() => {
    return buildings
      .filter((building) => building.name.toLowerCase().includes(searchBuilding.toLowerCase()))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [buildings, searchBuilding]);

  return (
    <>
      <div className="mb-2">
        <h2>Administrar edificios</h2>
      </div>
      <div className="d-flex justify-content-start mb-4">
        <input
          type="text"
          placeholder="Buscar por Nombre"
          value={searchBuilding}
          onChange={(e) => {
            const value = e.target.value;
            setSearchBuilding(value);
          }}
          className="w-50 p-3 border rounded"
        />
      </div>
      <div className="table-buildings">
        <table className="table table-striped">
          <thead className="text-center font-weight-bold">
            <tr>
              <th scope="col">Nombre</th>
              <th scope="col">Acción</th>
            </tr>
          </thead>
          <tbody>
            {filteredBuildings.map((building) => {
              const { id, name } = building;
              const isActiveMaintenance = building.maintenanceModeEndsAt && new Date(building.maintenanceModeEndsAt) > new Date();
              return (
                <tr key={id}>
                  <td className="align-middle">
                    {name}
                    {isActiveMaintenance && <span className="badge badge-warning ml-2">Mantenimiento</span>}
                  </td>
                  <td className="align-middle">
                    <button
                      type="button"
                      className={`btn btn-sm p-1 mr-1 d-inline-flex align-items-center justify-content-center ${isActiveMaintenance ? 'btn-warning' : ''}`}
                      title={isActiveMaintenance ? "Cancelar Mantenimiento" : "Activar Mantenimiento"}
                      onClick={() => toggleMaintenanceMode(building)}
                    >
                      <span className="material-icons">{isActiveMaintenance ? 'build_circle' : 'build'}</span>
                    </button>
                    <button type="button" className="btn btn-sm p-1 mr-1 d-inline-flex align-items-center justify-content-center" onClick={() => confirmMuteBuilding(building)}>
                      <span className="material-icons">volume_off</span>
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm p-1 mr-1 d-inline-flex align-items-center justify-content-center"
                      onClick={() => {
                        history.push({
                          pathname: `/admin-dashboard/buildings/${id}`,
                          state: building,
                        });
                      }}
                    >
                      <span className="material-icons">visibility</span>
                    </button>

                    <div className="dropdown d-sm-none d-inline-block">
                      <button
                        className="btn btn-sm dropdown-toggle d-inline-flex align-items-center justify-content-center"
                        type="button"
                        id="dropdownMenuButton"
                        data-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded="false"
                      ></button>
                      <div className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                        <button
                          className="dropdown-item d-flex align-items-center"
                          onClick={() => toggleMaintenanceMode(building)}
                        >
                          <span className="material-icons mr-2">{isActiveMaintenance ? 'build_circle' : 'build'}</span>
                          {isActiveMaintenance ? 'Cancelar Mantenimiento' : 'Activar Mantenimiento'}
                        </button>
                        <button
                          className="dropdown-item d-flex align-items-center"
                          onClick={() => {
                            history.push(`/admin-dashboard/buildings/${id}/apartments`);
                          }}
                        >
                          <span className="material-icons mr-2">edit</span>
                          Editar
                        </button>
                        <button
                          className="dropdown-item d-flex align-items-center"
                          onClick={() => {
                            history.push({
                              pathname: `/admin-dashboard/events/event-intrusion/${id}`,
                              state: building,
                            });
                          }}
                        >
                          <span className="material-icons mr-2">transfer_within_a_station</span>
                          Intrusos en cámaras
                        </button>
                        <button
                          className="dropdown-item d-flex align-items-center"
                          onClick={() => {
                            history.push({
                              pathname: `/admin-dashboard/events/open-door/${id}`,
                              state: building,
                            });
                          }}
                        >
                          <span className="material-icons mr-2">door_sliding</span>
                          Detalles OPEN_DOOR
                        </button>
                        <button
                          className="dropdown-item d-flex align-items-center"
                          onClick={() => confirmRestartRpiBuilding(building)}
                        >
                          <span className="material-icons mr-2">refresh</span>
                          Reiniciar RPI
                        </button>
                      </div>
                    </div>

                    <span className="d-none d-sm-inline">
                      <button
                        type="button"
                        className="btn btn-sm p-1 mr-1 d-inline-flex align-items-center justify-content-center"
                        onClick={() => {
                          history.push(`/admin-dashboard/buildings/${id}/apartments`);
                        }}
                      >
                        <span className="material-icons">edit</span>
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm p-1 mr-1 d-inline-flex align-items-center justify-content-center"
                        onClick={() => {
                          history.push({
                            pathname: `/admin-dashboard/events/event-intrusion/${id}`,
                            state: building,
                          });
                        }}
                      >
                        <span className="material-icons">transfer_within_a_station</span>
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm p-1 mr-1 d-inline-flex align-items-center justify-content-center"
                        onClick={() => {
                          history.push({
                            pathname: `/admin-dashboard/events/open-door/${id}`,
                            state: building,
                          });
                        }}
                      >
                        <span className="material-icons">door_sliding</span>
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm p-1 mr-1 d-inline-flex align-items-center justify-content-center"
                        onClick={() => confirmRestartRpiBuilding(building)}
                      >
                        <span className="material-icons">refresh</span>
                      </button>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};
Buildings.propTypes = {
  history: PropTypes.object,
  match: PropTypes.object,
};
export default Buildings;
